import { useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Link } from "@/components/i18n/LocaleLink";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { getStoredConsent, setStoredConsent, type ConsentChoice } from "@/lib/consent";

/**
 * Global cookie-consent banner. Shown once per browser (until a choice is made) on every locale.
 * There's no ad SDK to gate yet — this exists so the choice is captured and available via
 * hasAdConsent() before any ad/analytics code ships, and so the Cookie Policy's "consent
 * banner/preferences center" reference isn't describing something that doesn't exist.
 */
export function CookieConsentBanner() {
  const { t } = useLocale();
  const [choice, setChoice] = useState<ConsentChoice | null>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setChoice(getStoredConsent());
    setHydrated(true);
  }, []);

  if (!hydrated || choice !== null) return null;

  function decide(next: ConsentChoice) {
    setStoredConsent(next);
    setChoice(next);
  }

  return (
    <section
      aria-label={t("consent.bannerLabel")}
      className="fixed inset-x-0 bottom-0 z-50 border-t-4 border-white bg-foreground/95 px-4 py-4 text-background shadow-[0_-8px_30px_rgba(0,0,0,.25)] backdrop-blur sm:px-6"
    >
      <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <Cookie className="mt-0.5 size-5 shrink-0 text-primary" />
          <p className="text-sm font-medium leading-relaxed">
            {t("consent.message")}{" "}
            <Link to="/cookies" className="font-bold underline underline-offset-2 hover:text-primary">
              {t("consent.learnMore")}
            </Link>
          </p>
        </div>
        <div className="flex w-full shrink-0 gap-2 sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 border-white/30 bg-transparent text-background hover:bg-white/10 hover:text-background sm:flex-none"
            onClick={() => decide("rejected")}
          >
            {t("consent.rejectCta")}
          </Button>
          <Button size="sm" className="flex-1 sm:flex-none" onClick={() => decide("accepted")}>
            {t("consent.acceptCta")}
          </Button>
        </div>
      </div>
    </section>
  );
}
